import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, Wrench, Landmark, Home, CheckCircle, Star, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

export default function ClevelandProvidersPage() {
  const providerTypes = [
    {
      icon: Wrench,
      title: 'Contractors',
      description: 'General contractors, rehabbers, handymen, and specialty trades serving Cleveland investors.',
      link: '/directory?category=Contractor&area=Cleveland'
    },
    {
      icon: Landmark,
      title: 'Lenders',
      description: 'Hard money lenders, private lenders, and investor-friendly mortgage brokers.',
      link: '/directory?category=Lender&area=Cleveland'
    },
    {
      icon: Home,
      title: 'Property Managers',
      description: 'Property management companies specializing in investment properties and rentals.',
      link: '/directory?category=Property Manager&area=Cleveland'
    },
    {
      icon: Building2,
      title: 'Other Services',
      description: 'Title companies, inspectors, appraisers, and other investor-essential services.',
      link: '/directory?area=Cleveland'
    }
  ];

  const benefits = [
    'Get found by active Cleveland investors',
    'Receive direct inquiries and leads',
    'Build your reputation with reviews',
    'No monthly fees or commissions',
    'Showcase your work and specialties',
    'Connect with repeat clients'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Service providers"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-[#5DADE2]" />
              <span className="text-[#5DADE2] font-medium">Cleveland, Ohio</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Serve Cleveland's Investor Community
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              List your business in our directory and connect with Cleveland's active real estate investors. Contractors, lenders, and service providers welcome.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8 text-lg h-14">
                <Link to="/signup?role=provider">
                  List Your Business
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/directory?area=Cleveland">
                  Browse Cleveland Providers
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Provider Types */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Who Should Join?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              If you serve real estate investors in Cleveland, you belong in our directory.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {providerTypes.map((type, i) => (
              <Link key={i} to={type.link} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#5DADE2]/50 transition-all group">
                <div className="w-12 h-12 bg-[#5DADE2]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#5DADE2] transition-colors">
                  <type.icon className="h-6 w-6 text-[#5DADE2] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{type.title}</h3>
                <p className="text-slate-600 text-sm">{type.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6">
                Why List Your Business on NextFlip?
              </h2>
              <p className="text-slate-600 mb-8">
                Cleveland's investors are actively looking for reliable service providers. Get in front of them where they're already searching for deals.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button asChild className="mt-8 bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8">
                <Link to="/signup?role=provider">
                  Get Listed Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg">
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
                <div className="w-16 h-16 bg-slate-200 rounded-full"></div>
                <div>
                  <div className="font-semibold text-slate-900">Your Business Name</div>
                  <div className="text-sm text-slate-500">Cleveland, OH • Contractor</div>
                </div>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm text-slate-500 ml-2">5.0 (24 reviews)</span>
                </div>
                <p className="text-slate-600 text-sm">
                  "Your bio and description here. Highlight your experience, specialties, and what makes you the right choice for Cleveland investors..."
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 bg-[#5DADE2] text-white text-center py-2 rounded-lg text-sm font-medium">Contact</div>
                <div className="flex-1 bg-slate-100 text-slate-700 text-center py-2 rounded-lg text-sm font-medium">View Profile</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Grow Your Cleveland Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the providers already connecting with investors on NextFlip
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full px-8 text-lg h-14">
              <Link to="/signup?role=provider">
                List Your Business Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
